import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import Parse from 'parse';
import { returnUrlQueryParams } from 'src/app/auth/utils/return-url.util';
import { NgxPermissionsService } from 'ngx-permissions';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { clearImpersonationSessionTokens } from 'src/app/shared/utils/session-expiry.util';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private router: Router,
    private permissionService: NgxPermissionsService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return new Promise(async (resolve, reject) => {
      let currentUser;
      try {
        currentUser = Parse.User.current();
      } catch (error) {
        clearImpersonationSessionTokens();
        try {
          await Parse.User.logOut();
        } catch (error: any) {
          this.snackBar.open(error.message, this.translate.instant('Close'), {
            duration: 3000,
          });
        }

        this.redirectToLogin(state.url);
        return resolve(false);
      }

      if (currentUser) {
        try {
          const userInfo = await Parse.Cloud.run('getUser', {
            userId: Parse.User.current()?.id,
          });
          // Load all roles instead of just the highest priority one
          // This allows users with multiple roles (e.g., DGM + SPECIALIST_COORDINATOR)
          // to see nav items and access features for all their roles
          const rolesToLoad = [userInfo['role']];

          // Add additional PSC roles if user has them
          if (userInfo['roles']) {
            const pscRoles = [
              'ADMIN',
              'DGM',
              'CUSTOMER_MANAGER',
              'SPECIALIST_COORDINATOR',
              'COMPANY_MANAGER',
              'LOCATION_MANAGER',
            ];
            for (const roleString of userInfo['roles']) {
              for (const pscRole of pscRoles) {
                if (roleString === pscRole && !rolesToLoad.includes(pscRole)) {
                  rolesToLoad.push(pscRole);
                }
              }
            }
          }

          this.permissionService.loadPermissions(rolesToLoad);
          return resolve(true);
        } catch (error: any) {
          this.snackBar.open(error.message, this.translate.instant('Close'), {
            duration: 3000,
          });
          clearImpersonationSessionTokens();
          try {
            await Parse.User.logOut();
          } catch (error: any) {
            this.snackBar.open(error.message, this.translate.instant('Close'), {
              duration: 3000,
            });
          }

          this.redirectToLogin(state.url);
          return resolve(false);
        }
      } else {
        this.redirectToLogin(state.url);
        return resolve(false);
      }
    });
  }

  private redirectToLogin(returnUrl: string): void {
    this.router.navigate(['/auth/login'], {
      queryParams: returnUrlQueryParams(returnUrl),
    });
  }
}
